import apiClient from '../utils/apiClient';

/**
 * Point API Services
 * Quản lý điểm thưởng/phạt, khiếu nại và thống kê
 */

export const pointAPI = {
  /**
   * Lấy danh sách điểm với phân trang và lọc
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.employee_id - ID nhân viên
   * @param {string} params.month - Tháng (YYYY-MM)
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách điểm và metadata phân trang
   */
  getPoints: async (params = {}) => {
    const {
      employee_id,
      month,
      page = 1,
      limit = 10,
      type,
      appeal_status,
      branch_id,
      status,
      source_type,
    } = params;
    const q = { page, limit };
    if (employee_id != null && String(employee_id).trim() !== '') {
      q.employee_id = String(employee_id).trim();
    }
    if (month) q.month = month;
    if (type) q.type = type;
    if (appeal_status) q.appeal_status = appeal_status;
    if (branch_id) q.branch_id = branch_id;
    if (status) q.status = status;
    if (source_type) q.source_type = source_type;
    return await apiClient.get({
      url: '/points',
      params: q,
    });
  },

  /**
   * Lấy thông tin chi tiết điểm theo ID
   * @param {string} pointId - ID điểm
   * @returns {Promise<Object>} Thông tin chi tiết điểm
   */
  getPointById: async (pointId) => {
    return await apiClient.get({
      url: `/points/${pointId}`,
    });
  },

  /**
   * Lấy tổng điểm của nhân viên trong tháng
   * @param {string} employeeId - ID nhân viên
   * @param {string} month - Tháng (YYYY-MM)
   * @returns {Promise<Object>} Tổng điểm của nhân viên
   */
  getPointSummary: async (employeeId, month) => {
    return await apiClient.get({
      url: '/points/summary',
      params: { employee_id: employeeId, month },
    });
  },

  /**
   * Thêm điểm thủ công (MANAGER|ADMIN)
   * @param {Object} pointData - Dữ liệu điểm
   * @param {string} pointData.employee_id - ID nhân viên
   * @param {string} pointData.type - Loại điểm (ADD, SUBTRACT)
   * @param {number} pointData.value - Giá trị điểm
   * @param {string} pointData.reason - Lý do
   * @returns {Promise<Object>} Thông tin điểm vừa tạo
   */
  createManualPoint: async (pointData) => {
    return await apiClient.post({
      url: '/points',
      data: pointData,
    });
  },

  /**
   * Khiếu nại điểm
   * @param {string} pointId - ID điểm
   * @param {string} userId - ID người dùng hiện tại
   * @param {string} reason - Lý do khiếu nại
   * @returns {Promise<Object>} Thông tin điểm sau khi khiếu nại
   */
  appealPoint: async (pointId, userId, reason) => {
    return await apiClient.post({
      url: `/points/${pointId}/appeal`,
      data: { current_user_id: userId, reason },
    });
  },

  /**
   * Duyệt/từ chối khiếu nại điểm (MANAGER|ADMIN)
   * @param {string} pointId - ID điểm
   * @param {string} managerId - ID quản lý
   * @param {boolean} approve - Duyệt hay từ chối
   * @returns {Promise<Object>} Thông tin điểm sau khi duyệt khiếu nại
   */
  reviewAppeal: async (pointId, managerId, approve, reduceTo, responsibleEmployeeId) => {
    const data = { current_user_id: managerId, approve };
    if (reduceTo !== undefined) data.reduce_to = reduceTo;
    // #005638: chỉ gửi khi ADMIN thật sự đổi người chịu trách nhiệm — bỏ trống ⇒ BE giữ nguyên
    // hành vi cũ (phạt `created_by` của lần kiểm CLOSING ca trước).
    if (responsibleEmployeeId) data.responsible_employee_id = responsibleEmployeeId;
    return await apiClient.post({
      url: `/points/${pointId}/appeal-review`,
      data,
    });
  },

  /**
   * #005638 — NV có chấm công trùng khung giờ ca CLOSING bị báo cáo, để ADMIN chọn đúng người
   * chịu trách nhiệm khi duyệt khiếu nại "Báo cáo ca trước kiểm kê NVL sai". ADMIN only.
   */
  getResponsibleCandidates: async (pointId) => {
    return await apiClient.get({
      url: `/points/${pointId}/responsible-candidates`,
    });
  },

  getPendingAppeals: async (params = {}) => {
    const { branch_id, page = 1, limit = 20 } = params;
    const q = { page, limit };
    if (branch_id) q.branch_id = branch_id;
    return await apiClient.get({
      url: '/points/pending-appeals',
      params: q,
    });
  },

  getAppealContext: async (pointId) => {
    return await apiClient.get({
      url: `/points/${pointId}/appeal-context`,
    });
  },

  /**
   * Hủy điểm (ADMIN only)
   * @param {string} pointId - ID điểm
   * @param {string} reason - Lý do hủy
   * @returns {Promise<Object>} Kết quả hủy điểm
   */
  cancelPoint: async (pointId, reason) => {
    return await apiClient.patch({
      url: `/points/${pointId}/cancel`,
      data: { reason },
    });
  },

  /**
   * Lấy thống kê điểm theo chi nhánh/tháng
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.month - Tháng (YYYY-MM)
   * @returns {Promise<Object>} Thống kê điểm
   */
  getPointAnalytics: async (params = {}) => {
    const { branch_id = '', month = '' } = params;
    return await apiClient.get({
      url: '/points/analytics',
      params: { branch_id, month },
    });
  },

  /**
   * Lấy danh sách khiếu nại điểm
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.status - Trạng thái khiếu nại (PENDING, APPROVED, REJECTED)
   * @param {string} params.branch_id - ID chi nhánh
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách khiếu nại và metadata phân trang
   */
  getPointAppeals: async (params = {}) => {
    const { status = '', branch_id = '', page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: '/points/appeals',
      params: { status, branch_id, page, limit },
    });
  },

  /**
   * Lấy lịch sử thay đổi tỉ lệ thưởng doanh thu (revenue_reward_rate) của nhân viên
   * @param {string} employeeId - ID nhân viên
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.from_date - Ngày bắt đầu (YYYY-MM-DD)
   * @param {string} params.to_date - Ngày kết thúc (YYYY-MM-DD)
   * @param {string} params.direction - Loại thay đổi (ADD, DEDUCT)
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Lịch sử tỉ lệ thưởng và metadata phân trang
   */
  getEmployeePointHistory: async (employeeId, params = {}) => {
    const { from_date, to_date, direction, change_type, page = 1, limit = 10 } = params;
    const q = { page, limit };
    if (from_date) q.from_date = from_date;
    if (to_date) q.to_date = to_date;
    if (direction) q.direction = direction;
    if (change_type) q.change_type = change_type;
    return await apiClient.get({
      url: `/employees/${employeeId}/point-history`,
      params: q,
    });
  },

  /**
   * Lấy lịch sử ĐIỂM THƯỞNG/PHẠT của 1 nhân viên (bảng Point — gộp mọi nguồn: chấm tay ở
   * /points, xử lý QC ở /qc-results, và nguồn hệ thống tự sinh) — #221938.
   * KHÁC `getEmployeePointHistory` ở trên: hàm kia là lịch sử TỈ LỆ thưởng doanh thu (%).
   * @param {string} employeeId - ID nhân viên
   * @param {Object} params - from_date, to_date, type (ADD/SUBTRACT), source_type, status, page, limit
   * @returns {Promise<Object>} { data, total, page, limit, totals }
   */
  getEmployeeRewardPointHistory: async (employeeId, params = {}) => {
    const { from_date, to_date, type, source_type, status, page = 1, limit = 10 } = params;
    const q = { page, limit };
    if (from_date) q.from_date = from_date;
    if (to_date) q.to_date = to_date;
    if (type) q.type = type;
    if (source_type) q.source_type = source_type;
    if (status) q.status = status;
    return await apiClient.get({
      url: `/employees/${employeeId}/reward-point-history`,
      params: q,
    });
  },

  /**
   * Lấy bảng xếp hạng điểm theo chi nhánh/tháng
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.month - Tháng (YYYY-MM)
   * @param {number} params.limit - Số lượng top (default: 10)
   * @returns {Promise<Array>} Bảng xếp hạng điểm
   */
  getPointLeaderboard: async (params = {}) => {
    const { branch_id = '', month = '', limit = 10 } = params;
    return await apiClient.get({
      url: '/points/leaderboard',
      params: { branch_id, month, limit },
    });
  },

  /**
   * Export báo cáo điểm
   * @param {Object} params - Tham số export
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.month - Tháng (YYYY-MM)
   * @param {string} params.format - Định dạng export (pdf, excel)
   * @returns {Promise<Blob>} File export
   */
  exportPointReport: async (params = {}) => {
    const { branch_id = '', month = '', format = 'pdf' } = params;
    return await apiClient.get({
      url: '/points/export',
      params: { branch_id, month, format },
      responseType: 'blob',
    });
  },
};
